import React, { useEffect, useState } from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Overflow,
  Text,
} from '@gousto-internal/citrus-react'

import { useNumRecipesOverBasketLimit } from 'routes/Menu/domains/basket'

import { TooManyRecipesWarning } from '../../TooManyRecipesWarning'
import { TreatmentProgressMessage } from './TreatmentProgressMessage'

type RecipesInBasketProgressTreatmentContentProps = {
  selectedRecipesCount: number
  percentage: number
}

const getProgressColor = (percentage: number) => {
  if (percentage >= 100) {
    return Color.Success_600
  }

  if (percentage >= 80) {
    return Color.Warning_500
  }

  return Color.Error_800
}

const RecipesInBasketProgressTreatmentContent = ({
  selectedRecipesCount,
  percentage,
}: RecipesInBasketProgressTreatmentContentProps) => {
  const numRecipesOverLimit = useNumRecipesOverBasketLimit()
  const isBasketFull = percentage === 100
  const progressColor = getProgressColor(percentage)
  const [fullAnimationKey, setFullAnimationKey] = useState(0)
  const [isFullStateVisible, setIsFullStateVisible] = useState(false)

  useEffect(() => {
    if (!isBasketFull) {
      setIsFullStateVisible(false)

      return undefined
    }

    setIsFullStateVisible(false)
    setFullAnimationKey((currentKey) => currentKey + 1)

    const animationTimeout = window.setTimeout(() => {
      setIsFullStateVisible(true)
    }, 60)

    return () => {
      window.clearTimeout(animationTimeout)
    }
  }, [isBasketFull])

  if (numRecipesOverLimit) {
    return <TooManyRecipesWarning numRecipesOverLimit={numRecipesOverLimit} />
  }

  if (isBasketFull) {
    return (
      <Box
        key={fullAnimationKey}
        display={Display.Flex}
        alignItems={AlignItems.Center}
        gap={2}
        width="100%"
        color={Color.ColdGrey_800}
        style={{
          opacity: isFullStateVisible ? 1 : 0.6,
          transform: isFullStateVisible ? 'translateY(0)' : 'translateY(4px)',
          transition: 'opacity 220ms ease-out, transform 220ms ease-out',
        }}
      >
        <Box
          bg={Color.Success_600}
          flexShrink={0}
          width="6px"
          style={{
            alignSelf: 'stretch',
            marginLeft: '-12px',
            marginTop: '-8px',
            marginBottom: '-8px',
            borderTopLeftRadius: '3px',
            borderBottomLeftRadius: '3px',
          }}
        />
        <Box
          data-testid="tick"
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          minWidth="2rem"
          width="2rem"
          height="2rem"
          flexShrink={0}
          style={{
            opacity: isFullStateVisible ? 1 : 0,
            transform: isFullStateVisible ? 'scale(1)' : 'scale(0.8)',
            transition: 'opacity 220ms ease-out, transform 220ms ease-out',
          }}
        >
          <Icon name="task_alt" variant={IconVariant.Confirmation} size={6} />
        </Box>
        <Box style={{ whiteSpace: 'nowrap' }}>
          <TreatmentProgressMessage numRecipes={selectedRecipesCount} />
        </Box>
      </Box>
    )
  }

  return (
    <Box width="100%" display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        gap={2}
        data-no-code="boxProgressMessageRow"
      >
        <Box data-no-code="boxProgressPercentageWrapper">
          <Text
            color={Color.ColdGrey_800}
            fontWeight={FontWeight.Bold}
            size={2}
            data-no-code="boxProgressPercentage"
          >
            {percentage}%
          </Text>
        </Box>
        <Box data-no-code="boxProgressMessage">
          <TreatmentProgressMessage numRecipes={selectedRecipesCount} />
        </Box>
      </Box>
      <Box
        bg={Color.NeutralGrey_100}
        borderRadius={1.5}
        height="6px"
        overflowX={Overflow.Hidden}
        overflowY={Overflow.Hidden}
        width="100%"
      >
        <Box
          bg={progressColor}
          borderRadius={1.5}
          height="100%"
          width={`${percentage}%`}
          style={{ transition: 'width 500ms ease, background-color 500ms ease' }}
        />
      </Box>
    </Box>
  )
}

export { RecipesInBasketProgressTreatmentContent }
